import { Component, Input } from '@angular/core';
import {
  BowlingFrame,
  BowlingParticipant
} from '../../../interfaces/bowling.interface';

/**
 * One side's mini-scorecard — a header (name + total) plus a flat row
 * of 10 cells separated by thin vertical lines, one per frame. No
 * per-frame boxes, no cumulative-line below; the design is
 * intentionally minimal so two scorecards (sides A and B) fit
 * side-by-side in the translucent overlay above the 3D scene.
 *
 * Pure presentational component — `@Input` driven, no service
 * dependencies, no internal state. The parent
 * (`BowlingGameComponent.html`) renders one instance per side and
 * binds `isActive` to highlight whichever side is bowling next.
 */
@Component({
  selector: 'app-bowling-scorecard',
  templateUrl: './bowling-scorecard.component.html',
  styleUrls: ['./bowling-scorecard.component.scss']
})
export class BowlingScorecardComponent {
  /** The side this scorecard renders — required. Drives the name,
   *  colour accent, frames row, and total. */
  @Input() participant!: BowlingParticipant;
  /** Frame index of the bowler whose roll is "up next" — `-1` means
   *  none (the side's not the active one this turn). The matching
   *  cell in the row gets the `.now` highlight. */
  @Input() currentFrame: number = -1;
  /** Bound from parent: `state.currentSide === thisSide`. Adds the
   *  `.active` CSS class which lights up the side-coloured border +
   *  glow so the player can see whose turn it is at a glance. */
  @Input() isActive = false;
  /** Active language ('ar' or 'en'). Drives the bilingual `displayName`
   *  pick in the template. */
  @Input() currLang: any = 'en';

  /** Defensive accessor — returns the participant's frames array, or
   *  an empty list if the participant isn't bound yet (initial CD
   *  pass before parent has populated the input). */
  frames(): BowlingFrame[] {
    return this.participant?.frames ?? [];
  }

  /**
   * Render one roll value as the symbol shown in its cell:
   *   - empty string → frame not yet bowled
   *   - `'X'`        → strike (10 pins)
   *   - `'-'`        → gutter (0 pins)
   *   - the digit    → 1–9 pins
   *
   * The single-roll-per-frame model means there's never a "spare"
   * symbol (`/`) to render.
   */
  rollSymbol(frame: BowlingFrame): string {
    if (frame.roll1 === null) return '';
    if (frame.roll1 === 10) return 'X';
    if (frame.roll1 === 0) return '-';
    return String(frame.roll1);
  }

  /**
   * Cumulative as a plain string for binding (template doesn't
   * render `null`). Currently unused since the redesigned scorecard
   * dropped the cumulative line per cell, but kept for future use
   * if we re-add per-frame totals.
   */
  displayCumulative(frame: BowlingFrame): string {
    return frame.cumulativeScore === null ? '' : String(frame.cumulativeScore);
  }
}
